"use client";

import { motion } from "motion/react";
import { useTranslations } from "next-intl";
import { Icon } from "@iconify/react";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";

const USES = [
    { icon: "chart-2-bold-duotone", t: "Bs_everywhere_u1_t", d: "Bs_everywhere_u1_d" },
    { icon: "login-3-bold", t: "Bs_everywhere_u2_t", d: "Bs_everywhere_u2_d" },
    { icon: "window-frame-bold-duotone", t: "Bs_everywhere_u3_t", d: "Bs_everywhere_u3_d" },
    { icon: "cart-large-bold-duotone", t: "Bs_everywhere_u4_t", d: "Bs_everywhere_u4_d" },
    { icon: "settings-bold", t: "Bs_everywhere_u5_t", d: "Bs_everywhere_u5_d" },
    { icon: "user-rounded-bold-duotone", t: "Bs_everywhere_u6_t", d: "Bs_everywhere_u6_d" },
];

const PROVENANCE = [
    { name: "Twitter", logo: "logos:twitter" },
    { name: "GitHub", logo: "logos:github" },
    { name: "npm", logo: "logos:npm" },
];

const gridContainer = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.08, delayChildren: 0.12 } },
};
const cardItem = {
    hidden: { opacity: 0, y: 16, scale: 0.96 },
    visible: {
        opacity: 1,
        y: 0,
        scale: 1,
        transition: { duration: 0.45, ease: "easeOut" },
    },
};

export default function BsEverywhere() {
    const t = useTranslations();

    return (
        <section id="bs-everywhere" className="pt-16">
            <h2
                className="text-2xl dark:text-white font-bold font-sourceSans"
                style={{ marginBottom: "0.5em" }}
            >
                {t("Bs_everywhere_title")}
            </h2>
            <div className="w-20 h-1 bg-primary rounded mt-3 mb-5"></div>

            <p className="text-black dark:text-lightBlue max-w-[700px] leading-7 font-geistSans mb-0">
                {t("Bs_everywhere_intro")}
            </p>

            <div className="mt-10 relative rounded-3xl overflow-hidden bg-gradient-to-br from-bgDark-700 via-bgDark-800 to-bgDark-900 ring-1 ring-white/10 p-8 sm:p-12">
                {/* Glows */}
                <div className="absolute -top-24 -right-20 w-[460px] h-[460px] bg-purple-500/20 rounded-full blur-3xl pointer-events-none" />
                <div className="absolute -bottom-24 -left-20 w-[380px] h-[380px] bg-indigo-500/10 rounded-full blur-3xl pointer-events-none" />

                <div className="relative">
                    {/* Eyebrow */}
                    <div className="inline-flex items-center gap-2 rounded-full bg-purple-500/15 ring-1 ring-purple-400/30 px-3 py-1 mb-4">
                        <SolarIcon
                            name="bolt-bold-duotone"
                            className="w-3.5 h-3.5 text-purple-300"
                        />
                        <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-purple-300">
                            {t("Bs_everywhere_eyebrow")}
                        </span>
                    </div>

                    {/* Headline */}
                    <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white leading-[1.12] tracking-tight mb-8 max-w-[640px]">
                        {t.rich("Bs_everywhere_headline", {
                            highlight: (chunks) => (
                                <span className="bg-gradient-to-r from-purple-300 to-indigo-200 bg-clip-text text-transparent">
                                    {chunks}
                                </span>
                            ),
                        })}
                    </h3>

                    {/* Grille des usages */}
                    <motion.div
                        variants={gridContainer}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, margin: "-80px" }}
                        className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-5"
                    >
                        {USES.map((u) => (
                            <motion.div
                                key={u.t}
                                variants={cardItem}
                                className="rounded-2xl bg-white/[0.04] ring-1 ring-white/10 p-5 hover:bg-white/[0.07] transition-colors duration-200"
                            >
                                <div className="w-11 h-11 rounded-xl bg-purple-500/15 ring-1 ring-purple-400/30 flex items-center justify-center mb-4">
                                    <SolarIcon
                                        name={u.icon}
                                        className="w-5 h-5 text-purple-300"
                                    />
                                </div>
                                <h4 className="text-base font-semibold text-white mb-1 leading-tight">
                                    {t(u.t)}
                                </h4>
                                <p className="text-sm text-gray-300/80 leading-relaxed mb-0">
                                    {t(u.d)}
                                </p>
                            </motion.div>
                        ))}
                    </motion.div>

                    {/* Preuve sociale + provenance */}
                    <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-60px" }}
                        transition={{ duration: 0.5, ease: "easeOut", delay: 0.1 }}
                        className="mt-8 rounded-2xl bg-white/[0.04] ring-1 ring-white/10 px-5 py-5"
                    >
                        <div className="flex items-start gap-3">
                            <SolarIcon
                                name="verified-check-bold"
                                className="w-5 h-5 text-purple-300 shrink-0 mt-0.5"
                            />
                            <p className="text-sm text-gray-300/90 leading-relaxed mb-0">
                                {t.rich("Bs_everywhere_proof", {
                                    b: (chunks) => (
                                        <strong className="text-white font-semibold">
                                            {chunks}
                                        </strong>
                                    ),
                                })}
                            </p>
                        </div>
                        <div className="mt-4 flex flex-wrap items-center gap-2.5">
                            {PROVENANCE.map((p) => (
                                <span
                                    key={p.name}
                                    className="inline-flex items-center gap-2 rounded-lg bg-white ring-1 ring-black/5 px-2.5 py-1.5"
                                >
                                    <Icon
                                        icon={p.logo}
                                        className="w-4 h-4"
                                        aria-hidden="true"
                                    />
                                    <span className="text-[12px] font-semibold text-slate-700">
                                        {p.name}
                                    </span>
                                </span>
                            ))}
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
}
